import { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { APPLICATION_PAGE, JOB_SHARE_REF, setApplicationPageAndType } from '@/features/jobs/utils/jobOrigin';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '@/components/layout/PageLayout';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { ROUTES } from '@/app/router/routes';
import { useHome } from './hooks/useHome';
import { useOpenApplication } from '@/features/jobs/hooks/useOpenApplication';
import { HomeBanner } from './components/HomeBanner';
import { InfoTip } from '@/components/feedback/InfoTip';
import { JobSearchBar } from './components/JobSearchBar';
import { DataPrivacyDialog } from './components/DataPrivacyDialog';
import { JobCategorySection } from './components/JobCategorySection';
import { ExploreJobsSection } from './components/ExploreJobsSection';
import { Toast } from './components/Toast';
import { extractResumeSkills } from '@/services/api/applicationApi';
import { analyticsService } from '@/services/analytics/analyticsService';
import { useTenantStore } from '@/store/tenantStore';
import { FILE_ACCEPT_STRING } from '@/types/application.types';
import {
  isAgreedWithDataPolicy,
  setAgreedWithDataPolicy,
  validateResumeFile,
} from '@/features/jobs/utils/resumeFile';
import type { JobEntity, SearchInput } from '@/types/job.types';
import styles from './HomePage.module.css';
import { setRelevanceData } from '@/features/jobs/utils/relevanceData';
import { HiredRequisitionsProvider, OnboardingEntryCard } from '@/features/onboarding/entry';

// Flutter ErrorConstants / WebUploadResume texts
const NO_CONTENT =
  'No data is extracted from your resume. Please upload new resume or enter skills above to search for jobs.';
const FILE_EXTRACTION_FAILED = 'Some error occurred while extracting the resume';
const UPLOAD_TOOLTIP = 'We accept DOC, DOCX,\n TXT, PDF, WPS less than 5MB';
const UPLOAD_WORK_TEXT =
  'Skills are extracted from the resume to connect you with the right opportunities';
/**
 * HomePage — main authenticated home/dashboard page.
 * Mirrors Flutter WebHomePage + WebCareerPage.
 *
 * Sections:
 * 1. Hero banner (tenant-configured image + tagline overlay)
 * 2. Upload Resume (with the formats tooltip) + Job Search Bar, and the upload hint text
 * 3. Jobs by category (tabbed category tiles)
 * 4. Explore Jobs (All/Hot, filters, sort) + sticky preview of the selected job
 *
 * Business logic preserved from Flutter:
 * - Banner image from tenant preferences (desktopBanner/mobileBanner)
 * - Banner clickable hotspot regions
 * - Job categories from tenant config with fallback defaults
 * - Search with skills/roles + location
 * - All Jobs / Hot Jobs toggle
 * - Aggregation-based filters
 * - Sort by filter
 * - Save / unsave job (authenticated)
 * - Applied/saved status cross-reference
 * - Infinite scroll pagination (page size 6)
 * - Language change refresh
 * - "View all" / "View less" for categories
 */

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state, actions } = useHome();
  const openApplication = useOpenApplication();

  const isMobile = useIsMobile();

  // ── Infinite scroll — mirrors Flutter scrollController listener ──
  useEffect(() => {
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if (scrollHeight - scrollTop - clientHeight < 1000) {
        actions.onLoadMore();
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [actions]);

  // ── Navigation handlers — preserve Flutter navigation exactly ──

  const handleJobTitleClick = useCallback(
    (entity: JobEntity) => {
      setApplicationPageAndType(APPLICATION_PAGE.CAREER_SEARCH, false);
      setRelevanceData(entity);
      // Flutter: navigator.navigateTo(CoreRoute.jobsRoute, launchOtherTab: true, ...)
      void analyticsService.logScreenView({ screenName: ROUTES.JOBS });
      window.open(
        `${ROUTES.JOBS}/${entity.displayId}?ref=${JOB_SHARE_REF.INTERNAL}`,
        '_blank',
      );
    },
    [],
  );

  // Flutter WebCareerPage.buildJobCard onApply(index, isEasyApply) — used by the detail panel
  // and, below the split layout, by each card's Apply / Easy Apply button
  const handleJobApplyClick = useCallback(
    (entity: JobEntity, _index: number, isEasyApply: boolean) => {
      setApplicationPageAndType(APPLICATION_PAGE.CAREER_SEARCH, false);
      openApplication(entity.displayId, isEasyApply, 'home_page');
    },
    [openApplication],
  );

  const handleSearchGo = useCallback(
    (input: SearchInput) => {
      void analyticsService.logJobSearch({
        searchTerm: 'job search',
        filters: state.searchInput.filters,
      });
      void analyticsService.logScreenView({ screenName: ROUTES.JOBS, screenClass: 'home_page' });
      navigate(ROUTES.JOBS, { state: { searchInput: input } });
    },
    [navigate, state.searchInput.filters],
  );

  const enableSigmaAI = !!(useTenantStore((s) => s.config) as Record<string, unknown> | null)
    ?.enableSigmaAI;

  /* ── Resume upload from Home — mirrors Flutter WebHomeBloc._SelectFilesFromSystem → _ExtractResume ── */
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [showDataPrivacy, setShowDataPrivacy] = useState(false);
  // The QR entry asks for consent every time without remembering it (Flutter _WebHomePageConsumer)
  const rememberConsent = useRef(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Flutter WebCareerPage upload button: skip the data-privacy dialog once the user has agreed
  const handleResumeUpload = useCallback(() => {
    if (isAgreedWithDataPolicy()) {
      void analyticsService.logEvent('career_page', 'click_on_upload_resume');
      openFilePicker();
      return;
    }
    rememberConsent.current = true;
    setShowDataPrivacy(true);
  }, [openFilePicker]);

  const handleDataPrivacyAgree = useCallback(() => {
    setShowDataPrivacy(false);
    if (rememberConsent.current) {
      setAgreedWithDataPolicy();
      void analyticsService.logEvent('career_page', 'click_on_upload_resume');
    }
    openFilePicker();
  }, [openFilePicker]);

  const handleDataPrivacyCancel = useCallback(() => setShowDataPrivacy(false), []);

  // /home?qr_scan=search — QR code entry straight into search-by-resume (Flutter routing_config homeRoute)
  useEffect(() => {
    if (searchParams.get('qr_scan') !== 'search') return;
    rememberConsent.current = false;
    setShowDataPrivacy(true);
    const next = new URLSearchParams(searchParams);
    next.delete('qr_scan');
    setSearchParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResumeFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';

      const fileError = validateResumeFile(file);
      if (fileError) {
        setUploadError(t(fileError));
        return;
      }

      setIsAnalyzing(true);
      setUploadError(null);
      try {
        const result = await extractResumeSkills(file);
        if (!result || typeof result !== 'object') {
          setUploadError(t(NO_CONTENT));
          return;
        }
        const extractData = result as Record<string, unknown>;
        extractData.fileName = file.name;
        sessionStorage.setItem('extractedResumeData', JSON.stringify(extractData));
        void analyticsService.logScreenView({ screenName: ROUTES.EXTRACTED_RESUME });
        navigate(ROUTES.EXTRACTED_RESUME);
      } catch {
        setUploadError(t(FILE_EXTRACTION_FAILED));
      } finally {
        setIsAnalyzing(false);
      }
    },
    [navigate, t],
  );

  return (
    <PageLayout>
      {/* Candidate onboarding (React only, FLUTTER_DEVIATIONS §5): the jobs the candidate is hired for, loaded once for the
          home card and the Onboarding In Progress buttons on the cards and the detail panel; nothing in prod */}
      <HiredRequisitionsProvider>
        {/* Hidden file input for resume upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept={FILE_ACCEPT_STRING}
          style={{ display: 'none' }}
          onChange={handleResumeFileChange}
        />

        {showDataPrivacy && (
          <DataPrivacyDialog onAgree={handleDataPrivacyAgree} onCancel={handleDataPrivacyCancel} />
        )}

        {/* Resume analyzing overlay — mirrors Flutter showAnalyzer */}
        {isAnalyzing && (
          <div className={styles.analyzerOverlay}>
            {enableSigmaAI ? (
              /* Flutter SigmaResumeAnalyzeWidget (tenant config enableSigmaAI) */
              <div className={styles.analyzerContent}>
                <img src="/assets/images/agent_sigma_loader.gif" alt="" width={210} height={210} />
                <p className={styles.analyzerText}>Matching You with the Right Opportunities</p>
                <p className={styles.analyzerSubtext}>
                  AI Agent Sigma is matching your profile to the best-fit roles.
                </p>
              </div>
            ) : (
              <div className={styles.analyzerContent}>
                <div className={styles.spinner} />
                <p className={styles.analyzerText}>{t('Analyzing')}</p>
                <p className={styles.analyzerSubtext}>{t('We are analyzing your profile, Please wait.')}</p>
              </div>
            )}
          </div>
        )}

        {/* 1. Hero Banner */}
        <HomeBanner isMobile={isMobile} />

        {/* Main content area */}
        <div className={styles.content}>
          {/* Candidate onboarding entry: renders nothing unless the feature is enabled (not in prod) */}
          <OnboardingEntryCard />

          {/* 2. Upload Resume + search — Flutter WebCareerPage row / UploadAndSearchBarColumn,
              in the redesign's "Find Your Next Role" card */}
          <div className={styles.sectionHead}>
            <span className={styles.sectionBar} aria-hidden="true" />
            <div>
              <h2 className={styles.sectionTitle}>{t('Find Your Next Role')}</h2>
              <p className={styles.sectionSub}>{t('Search, compare, and apply — all on one screen')}</p>
            </div>
          </div>
          <div className={styles.searchCard}>
            <div className={styles.resumeCta}>
              <div className={styles.resumeCopy}>
                <div className={styles.resumeKicker}>{t('Fastest way in')}</div>
                <div className={styles.resumeTitle}>{t('Match me from my resume')}</div>
              </div>
              <button
                type="button"
                className={styles.uploadBtn}
                onClick={handleResumeUpload}
                disabled={isAnalyzing}
              >
                <UploadIcon />
                {t('Upload Resume')}
                {/* Flutter Tooltip / ToolTipWithInfoIconWidget (WebUploadResume.toolTipMessage) */}
                <span className={styles.uploadInfo}><InfoTip message={t(UPLOAD_TOOLTIP)} /></span>
              </button>
            </div>
            <span className={styles.orText}>{t('or search')}</span>
            <div className={styles.searchBar}>
              <JobSearchBar onSearch={handleSearchGo} isMobile={isMobile} variant="inline" showSwitch />
            </div>
            {/* Flutter ResumeUploadButtonWorkTextWidget (WebUploadResume.resumeUploadButtonWorkText) */}
            <p className={styles.uploadHint}>{t(UPLOAD_WORK_TEXT)}</p>
          </div>

          {/* 3. Jobs by category */}
          <JobCategorySection
            searchInput={state.searchInput}
            categories={state.categoryData}
            selectedIndex={state.categoryTabIndex}
            showViewAll={state.showCategoryViewAll[state.categoryTabIndex] ?? false}
            isLoading={state.isLoadingCategories}
            onSelectTab={actions.onSelectCategoryTab}
            onToggleViewAll={actions.onToggleCategoryViewAll}
          />

          {/* 4. Jobs — list + selected job preview */}
          <ExploreJobsSection
            entities={state.entities}
            aggregations={state.aggregations}
            exploreTabIndex={state.exploreTabIndex}
            allJobsCount={state.allJobsCount}
            hotJobsCount={state.hotJobsCount}
            isFilterVisible={state.isFilterVisible}
            appliedFilterCount={state.appliedFilterCount}
            isLoadingJobs={state.isLoadingJobs}
            isLoadingMore={state.isLoadingMore}
            onSelectTab={actions.onSelectExploreTab}
            onToggleFilterPanel={actions.onToggleFilterPanel}
            onSaveJob={actions.onSaveJob}
            onUnsaveJob={actions.onUnsaveJob}
            onApplyFilters={actions.onApplyFilters}
            onClearFilters={actions.onClearFilters}
            onFilterSelection={actions.onFilterSelection}
            onSelectSort={actions.onSelectSort}
            onClearFilter={actions.onClearSingleFilter}
            onClickJobTitle={handleJobTitleClick}
            onClickApply={handleJobApplyClick}
          />
        </div>

        {/* Toast notifications */}
        {state.toast && (
          <Toast
            message={state.toast.message}
            type={state.toast.type}
            onClose={actions.clearToast}
          />
        )}
        {uploadError && (
          <Toast
            message={uploadError}
            type="error"
            onClose={() => setUploadError(null)}
          />
        )}
      </HiredRequisitionsProvider>
    </PageLayout>
  );
}

/** Flutter WebAssetConstants.uploadResumeIcon */
function UploadIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 13V3.5M6.5 7 10 3.5 13.5 7" />
      <path d="M3.5 12.5v2.5a1.5 1.5 0 0 0 1.5 1.5h10a1.5 1.5 0 0 0 1.5-1.5v-2.5" />
    </svg>
  );
}
